import { z } from 'zod';

/**
 * Lider (leader) assignment contracts — single source of truth for the wire
 * shapes shared between the API (`lider.service.ts` / `lider.routes.ts`) and the
 * web client (`apps/web/src/api/lider.ts`). Before this module the request shape
 * lived inline in the route and the warning/result shapes were duplicated as
 * hand-written interfaces on both sides; this consolidates them into Zod schemas
 * (repo convention: `packages/shared/src/schemas/` is the contract layer).
 *
 * Owner decision (2026-06-11, binding): ámbito/turno mismatches between the
 * leader and the ticket are LEGAL (shift-cover is a real operational scenario).
 * These are ADVISORY warnings — the UI surfaces them in a confirm before
 * assigning, but assignment NEVER blocks. The dryRun preview reports warnings
 * without writing.
 */

/**
 * Per-ticket mismatch warning between the leader being assigned and the ticket.
 *
 * Invariants enforced by the refines below:
 * - A warning only exists when there is at least one real mismatch
 *   (`ambitoMismatch || turnoMismatch`) — the producer never emits a warning
 *   with both flags false.
 * - A turno mismatch implies both sides are known: when `turnoMismatch` is true,
 *   `ticketTurno` and `leaderTurno` are non-null. Turno is nullable on both
 *   sides (a global admin / cubre-turnos has no registered turno; a ticket may
 *   have no derivable turno) — when either side is null there is no mismatch,
 *   same nullable semantics as the cuadrilla suggested-workers filter.
 */
export const AssignmentWarningSchema = z
  .object({
    ticketId: z.number().int().positive(),
    correlativo: z.string(),
    ambitoMismatch: z.boolean(),
    turnoMismatch: z.boolean(),
    /** Ticket ámbito name (for the confirm copy). */
    ticketAmbito: z.string(),
    /** Effective turno of the ticket (derived from its date), or null. */
    ticketTurno: z.string().nullable(),
    /** Registered turno of the leader, or null. */
    leaderTurno: z.string().nullable(),
  })
  .refine((w) => w.ambitoMismatch || w.turnoMismatch, {
    message: 'A warning must carry at least one mismatch',
  })
  .refine((w) => !w.turnoMismatch || (w.ticketTurno !== null && w.leaderTurno !== null), {
    message: 'A turno mismatch requires both ticketTurno and leaderTurno to be known',
  });

export type AssignmentWarning = z.infer<typeof AssignmentWarningSchema>;

/**
 * Result of a batch assignment.
 *
 * NOTE: in dryRun (preview) mode `assigned === 0` by construction — the preview
 * computes warnings without writing, so nothing is assigned. `skipped` lists the
 * requested ticket ids that did not resolve to a candidate ticket (deleted /
 * not found), in BOTH dryRun and committed modes.
 */
export const BatchAssignResultSchema = z.object({
  assigned: z.number().int().nonnegative(),
  skipped: z.array(z.number().int().positive()),
  warnings: z.array(AssignmentWarningSchema),
});

export type BatchAssignResult = z.infer<typeof BatchAssignResultSchema>;

/**
 * Request body for `POST /lider/asignar-batch`.
 *
 * `dryRun: true` computes ámbito/turno warnings WITHOUT assigning, so the UI can
 * show a confirm before committing. Assignment never blocks on mismatches.
 */
export const BatchAssignSchema = z.object({
  ticketIds: z.array(z.number().int().positive()).min(1).max(100),
  operadorId: z.number().int().positive(),
  dryRun: z.boolean().optional(),
});

export type BatchAssignInput = z.infer<typeof BatchAssignSchema>;
